import React, { memo, useEffect, useState } from 'react';
import { MultiSelect } from 'react-multi-select-component';
import { CFormFeedback, CFormLabel, CPopover } from '@coreui/react-pro';
import CIcon from '@coreui/icons-react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { jsonAndCompare } from '../../../utils';
import { DEFAULT_FN } from '../../../constants';

// const options = [
//      { label: 'Mango 🥭', value: 'mango' },
//      { label: 'Strawberry 🍓', value: 'strawberry', disabled: true },
// ];

const SimpleMultiSelectGroupV2 = ({
     id,
     label,
     name,
     className,
     isRequired,
     disabled,
     placeholder,
     tooltip,
     values,
     error,
     options,
     customValueRenderer,
     onChange,
     onMenuToggle,
     isDeleteItem,
     isLoading,
     isDisabledChange,
}) => {
     const [selected, setSelected] = useState(() => {
          if (!values.length) {
               return [];
          }

          return options.filter((option) => values.includes(option.value));
     });

     useEffect(() => {
          const newOptions = options.filter((option) => values.includes(option.value) || values.some(({ value }) => value === option.value));

          if (!jsonAndCompare(selected, newOptions)) {
               setSelected(newOptions);
          }
     }, [options]); // eslint-disable-line react-hooks/exhaustive-deps

     const handleCustomValueRenderer = (selected, options) => {
          if (!customValueRenderer) {
               if (selected.length === options.length) {
                    return 'All items are selected.';
               }

               return selected.length
                    ? selected.map(({ label }, index) => {
                           if (selected.length - 1 !== index) {
                                return label + ', ';
                           }

                           return label;
                      })
                    : placeholder;
          }

          return customValueRenderer(selected, options);
     };

     const handleChangeSelect = (value) => {
          if (isDisabledChange) return;

          setSelected(value);
          onChange(value);
     };

     const handleFilterOptions = (options, filter) => {
          if (!filter) {
               return options;
          }

          const regex = new RegExp(filter, 'i');
          return options.filter(({ label }) => {
               // Hide option empty and skip option empty when filter
               if (label === '$empty$') {
                    return true;
               }

               return label && label.match(regex);
          });
     };

     const handleRemoveSelectedItem = (index) => {
          const newOptions = [...selected];
          newOptions.splice(index, 1);

          setSelected(newOptions);
          onChange(newOptions);
     };

     return (
          <div className={classNames('form-group form-multi-select-component', { [className]: className, 'is-delete-item': isDeleteItem, disabled })}>
               {label && (
                    <CFormLabel htmlFor={name}>
                         {<span className="me-1">{label}</span>}
                         {tooltip && (
                              <CPopover content={tooltip} placement="top" trigger="hover">
                                   <CIcon icon="icon-info-cilcle" />
                              </CPopover>
                         )}
                         {isRequired && <span className="text-danger">*</span>}
                    </CFormLabel>
               )}
               <MultiSelect
                    id={id}
                    className={classNames({ 'is-invalid': isRequired && !!error })}
                    disabled={disabled}
                    options={options}
                    value={selected}
                    labelledBy={placeholder}
                    valueRenderer={handleCustomValueRenderer}
                    onChange={handleChangeSelect}
                    filterOptions={handleFilterOptions}
                    onMenuToggle={onMenuToggle}
                    isLoading={isLoading}
               />
               {selected.length > 0 && isDeleteItem && (
                    <ul className="selected-list">
                         {selected.map((selectedItem, selectedIndex) => (
                              <li key={selectedItem.value} className="selected-item">
                                   <span>{selectedItem.label}</span>
                                   <span className="selected-remove" onClick={() => handleRemoveSelectedItem(selectedIndex)}>
                                        &times;
                                   </span>
                              </li>
                         ))}
                    </ul>
               )}

               {isRequired && !!error && <CFormFeedback invalid>{error}</CFormFeedback>}
          </div>
     );
};

SimpleMultiSelectGroupV2.propTypes = {
     id: PropTypes.string,
     label: PropTypes.string,
     name: PropTypes.string,
     className: PropTypes.string,
     isRequired: PropTypes.bool,
     disabled: PropTypes.bool,
     placeholder: PropTypes.string,
     tooltip: PropTypes.string,
     options: PropTypes.array,
     values: PropTypes.array,
     error: PropTypes.string,
     customValueRenderer: PropTypes.func,
     onChange: PropTypes.func,
     onMenuToggle: PropTypes.func,
     isDeleteItem: PropTypes.bool,
     isLoading: PropTypes.bool,
     isDisabledChange: PropTypes.bool,
};

SimpleMultiSelectGroupV2.defaultProps = {
     options: [],
     values: [],
     isDeleteItem: true,
     placeholder: 'Select',
     onChange: DEFAULT_FN,
     onMenuToggle: DEFAULT_FN,
};

export default memo(SimpleMultiSelectGroupV2);
